/* eslint-disable no-bitwise */
import { Constants } from '@pwc/pwc-deals-web-common';
import React, { useCallback, useState } from 'react';
import { cond, constant, stubTrue } from 'lodash';
import { TextInput, Button, Loader, Icon } from '@pwc/pwc-deals-ui';
import { useUserSearch } from '../../hooks/useUserSearch';
import { useClickedOutsideOfRef } from '../../hooks/useClickedOutsideOfRef';
import './UserSearchSelect.scss';

const nameIsLongEnough = (name, startSearchingLength) =>
  (name || '').trim().length >= startSearchingLength;

const hasAvailableUsers = (availableUsers) =>
  !availableUsers || availableUsers.length === 0;

const renderLoadingIndicator = constant(
  <div className="user-search-select-loading">
    <Loader dataTestId="user-search-select-loading" />
  </div>
);

const renderErrorMessage = constant(
  <div className="user-search-select-error">
    <span>
      <Icon name="alert" className="fill icon-yellow" />
      We are currently unable to search for users.
    </span>
  </div>
);

const renderNotEnouchCharactersMessage = constant(
  <div className="user-search-select-no-results">
    <span>Enter More Characters To Search</span>
  </div>
);

const renderNoMatchingUsersFoundMessage = constant(
  <div className="user-search-select-no-results">
    <span>No Matching Users Found</span>
  </div>
);

const roleDescriptor = (role) => {
  switch (role) {
    case Constants.USER_ROLES.ENGAGEMENT_LEAD:
      return 'the Engagement Lead';
    case Constants.USER_ROLES.ENGAGEMENT_MANAGER:
      return 'the Engagement Manager';
    case Constants.USER_ROLES.PROJECT_ADMIN:
      return 'a Project Admin';
    case Constants.USER_ROLES.APPROVER:
      return 'an Approver';
    case Constants.USER_ROLES.TEAM_MEMBER:
      return 'a Team Member';
    case Constants.USER_ROLES.PWC_READONLY:
      return 'a Read-only user';
    case Constants.USER_ROLES.PWC_READONLY_DISPLAY:
      return 'a Read-only user';
    case Constants.USER_ROLES.CLIENT_ADMIN:
      return 'a Client Admin';
    case Constants.USER_ROLES.CLIENT_TEAM_MEMBER:
      return 'a Client Team Member';
    default:
      return 'already in a role';
  }
};

const alreadySelectedUserMessage = (user) => {
  return user.isDisabled ? (
    <>
      <p className="already-selected-user">
        {`This user is ${roleDescriptor(user.role)} on this project.`}
        <br />
        Remove them from that role to add them here.
      </p>
    </>
  ) : null;
};

const renderUsersList = (availableUsers, selectUser) => () => {
  return (
    <div className="user-search-select-list">
      <ul>
        {availableUsers.map((user) => (
          <li key={user.email}>
            <Button
              data-testid="user-search-select-add-user-button"
              onClick={() => selectUser(user)}
              type="button"
              disabled={user.isDisabled}
            >
              {`${user.firstName} ${user.lastName} (${user.email})`}
              {alreadySelectedUserMessage(user)}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const getUserSelectConfig = () => {
  return {
    maxResultCount: process.env.SEARCH_USER_MAX_RESULT_COUNT || 10,
    debounceTime: process.env.SEARCH_USER_DELAY_BEFORE_SEND_SEARCH || 250,
    startSearchingLength:
      process.env.SEARCH_USER_MIN_CHARACTERS_TO_SEND_SEARCH || 3,
  };
};

export const UserSearchSelect = ({
  role,
  onUserSelected,
  unavailableUsers,
  customValidators = [],
  placeHolderText = 'Name',
}) => {
  const [name, setName] = useState('');

  const roleIdPart = role.split(' ').join('-').toLowerCase();

  const config = getUserSelectConfig();

  const defaultValidator = (value) => {
    return value.trim().length >= config.startSearchingLength;
  };

  const searchValidators =
    customValidators.length > 0 ? customValidators : [defaultValidator];

  const { usersFound, searchUsers, clearUsers, isLoading, hasError } =
    useUserSearch(
      role,
      config.maxResultCount,
      config.debounceTime,
      searchValidators
    );

  const availableUsers = !usersFound
    ? []
    : usersFound.map((user) => {
        const unavailableUser = unavailableUsers.find(
          (unUser) => unUser.email === user.email
        );

        let isDisabled = false;
        let userRole = '';
        if (unavailableUser) {
          isDisabled = true;
          userRole = unavailableUser.role;
        }

        return {
          ...user,
          isDisabled,
          role: userRole,
        };
      });

  const handleTextChange = useCallback(
    (event) => {
      const text = event.target.value;
      setName(text);
      searchUsers(text);
    },
    [searchUsers]
  );

  const resetSearch = () => {
    setName('');
    clearUsers();
  };

  const selectUser = (user) => {
    onUserSelected({ ...user, role });
    resetSearch();
  };

  const ref = useClickedOutsideOfRef(
    (wasClickedOutside) => wasClickedOutside && resetSearch()
  );

  const renderPopup = cond([
    [() => !name, constant(null)],
    [() => isLoading, renderLoadingIndicator],
    [() => hasError, renderErrorMessage],
    [
      () =>
        hasAvailableUsers(availableUsers) &&
        nameIsLongEnough(name, config.startSearchingLength),
      renderNoMatchingUsersFoundMessage,
    ],
    [() => hasAvailableUsers(availableUsers), renderNotEnouchCharactersMessage],
    [stubTrue, renderUsersList(availableUsers, selectUser)],
  ]);

  return (
    <div className="user-search-select" ref={ref}>
      <TextInput
        basic
        data-testid={`user-search-select-input-${roleIdPart}`}
        field={{
          name: `user-search-select-input-${roleIdPart}`,
          value: name || '',
          onChange: handleTextChange,
        }}
        type="text"
        autoComplete="off"
        iconLeft="search"
        placeholder={placeHolderText}
        className={name ? 'has-value' : ''}
      />
      {renderPopup()}
    </div>
  );
};
export default UserSearchSelect;
